import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { FulfillmentPlanFulfillmentType } from '../../../enums/fulfillment-plan-fulfillment-type';

import ShipmentStatus from '../components/shipment-status';
import TrackingInformation from '../components/tracking-information';
import DelegationDate from '../components/delegation-date';
import ShipDate from '../components/ship-date';
import ShipFrom from '../components/ship-from';
import Weight from '../components/weight';
import SelectedService from '../components/selected-service';
import RateShopper from '../components/rate-shopper';
import Package from '../components/package';
import Dimensions from '../components/dimensions';
import Confirmation from '../components/confirmation';
import Insurance from '../components/insurance';
import Rate from '../components/rate';
import TotalCost from '../components/total-cost';
import { ReturnError } from '../components/return-error';
import Carrier from '../components/carrier';
import ExternalFulfillmentCarrier from '../components/external-fulfillment-carrier';
import DutiesAndTaxes from '../components/duties-and-taxes';
import LocalDeliveryRateCard from './local-delivery-rate-card';
import { useIsServiceLocalDelivery } from '../../../hooks/use-is-service-local-delivery';
import Global from '../../../store/global-selectors';
import { calculateTotalInsuredValue } from '../../../services/insurance-service';
import { USD } from '../../../constants/currency-codes';
import RejectedBanner from '../../../components/rejected-banner';
import { useEnableDelegationDate } from '../../../feature-flags/hooks/use-enable-delegation-date';
import { PackageContentDescriptionReadOnly } from '../components/package-content-description-read-only';
import { willShipmentPassThroughMexico } from '../../../fulfillment-planner/package-content-description/use-should-require-package-content-description';

import style from '../components/style.module.less';

const SingleOutboundShipmentDisplay = ({ shipment, orderGridStatus, packagingDetails }) => {
  const {
    selectedService,
    trackingInformation,
    delegationDate,
    shipDate,
    insuranceProvider,
    packages,
    carrier = {},
    fulfillmentType,
    totalCost,
    confirmation,
    serviceCollectionGuid,
    availableShippingOptions = [],
    options,
    returnError,
  } = shipment;
  const { shipFrom, packageType, weight, dimensionsWithUnit } = packagingDetails;

  const isServiceLocalDelivery = useIsServiceLocalDelivery(shipment.labelConfiguration?.serviceId);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const enableDelegationDate = useEnableDelegationDate();
  const isPackageDescriptionRequired = willShipmentPassThroughMexico(shipment.shipFrom?.countryCode, shipment.shipTo?.countryCode);

  if (fulfillmentType === FulfillmentPlanFulfillmentType.Manual) {
    return (
      <div className={style['shipment-read-only']}>
        <div className={style['read-only-fields']}>
          <div className={style.fields}>
            <ShipmentStatus fulfillmentPlanId={shipment.fulfillmentPlanId} markedAsShipped showMdash={!orderGridStatus} />
            <TrackingInformation showMdash={!trackingInformation} trackingInformation={trackingInformation} />
            <Carrier carrierId={carrier.carrierId} />
          </div>
        </div>
      </div>
    );
  }

  if (fulfillmentType === FulfillmentPlanFulfillmentType.External) {
    return (
      <div className={style['shipment-read-only']}>
        <div className={style['read-only-fields']}>
          <div className={style.fields}>
            <RejectedBanner className={style['rejected-banner']} fulfillmentPlanId={shipment.fulfillmentPlanId} />
            <ShipmentStatus fulfillmentPlanId={shipment.fulfillmentPlanId} showMdash={!orderGridStatus} />
            <TrackingInformation showMdash={!trackingInformation} trackingInformation={trackingInformation} />
            {enableDelegationDate && !!delegationDate && <DelegationDate delegationDate={delegationDate} showMdash={!delegationDate} />}
            <ShipDate shipDate={shipDate} showMdash={!shipDate} />
            <ExternalFulfillmentCarrier carrierId={carrier.carrierId} carrierName={shipment.externalFulfillment?.carrierCode} />
            <SelectedService selectedService={selectedService} showMdash={!selectedService} />
            <Rate rate={totalCost} />
          </div>
        </div>
      </div>
    );
  }

  const insuredValue = calculateTotalInsuredValue(packages, insuranceProvider?.insuranceProviderId, sellerCurrencyCode);

  return (
    <div className={style['shipment-read-only']}>
      <div className={style['read-only-fields']}>
        <div className={style.fields}>
          {isServiceLocalDelivery ? (
            <LocalDeliveryRateCard packagingDetails={packagingDetails} shipment={shipment} />
          ) : (
            <>
              <ShipmentStatus fulfillmentPlanId={shipment.fulfillmentPlanId} showMdash={!orderGridStatus} />
              <TrackingInformation showMdash={!trackingInformation} trackingInformation={trackingInformation} />
              <RateShopper serviceCollectionGuid={serviceCollectionGuid} />
              <SelectedService selectedService={selectedService} showMdash={!selectedService} />
              <ShipDate shipDate={shipDate} showMdash={!shipDate} />
              <ShipFrom shipFrom={shipFrom} />
              <Weight showMdash={!weight} weight={weight} />
              <Dimensions dimensionsWithUnit={dimensionsWithUnit} showMdash={!dimensionsWithUnit} />
              <Package packageType={packageType} showMdash={!packageType} />
              <PackageContentDescriptionReadOnly isPackageDescriptionRequired={isPackageDescriptionRequired} packages={packages} />
              <Confirmation confirmation={confirmation} showMdash={!confirmation} />
              <Insurance insuranceProviderName={insuranceProvider} insuredValue={insuredValue} showMdash={!insuranceProvider} />
              <DutiesAndTaxes availableShippingOptions={availableShippingOptions} options={options} />
              <ReturnError returnError={returnError} />
              <TotalCost totalCost={totalCost} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

SingleOutboundShipmentDisplay.propTypes = {
  shipment: PropTypes.object.isRequired,
  orderGridStatus: PropTypes.object.isRequired,
  packagingDetails: PropTypes.object.isRequired,
};

export default SingleOutboundShipmentDisplay;
